import {
  NodeType,
  type RootNodeCategory,
  isExtendedRootNodeConfig,
  isRootNodeConfig,
} from '@nodeTypeConfig/configTypes';
import { nodeTypeDefinitions } from '@nodeTypeConfig/nodeTypeConfig';
import { nodeTypeConfigurations } from '../../config/nodeTypeConfigurations/index';
import { settings } from '../../config/settings';
import type { UserRole } from './userTypes';

export const getUrlsForPath = (resourcesRaw: string[], pathname: string): any[] => {
  return resourcesRaw
    .map((rawUrl) => new URL(`${settings.apisAndUrls.api}${rawUrl}`))
    .filter((url) => url.pathname === pathname);
};

const getAllowedContentTypesAndTags = (resourcesRaw: string[]): any => {
  const rRawContent = getUrlsForPath(resourcesRaw, '/content');

  let allowedContentTags: string[] = [];
  let allowedContentTypes: string[] = [];
  const allowedPublishedEdits: string[] = [];
  rRawContent.forEach((rawUrl) => {
    [...rawUrl.searchParams.entries()].forEach(([key, value]) => {
      if (['tagsIn', 'tags', 'rootTag'].includes(key)) {
        allowedContentTags = [...allowedContentTags, ...value.split(',')];
      } else if (['typeIn', 'type', 'rootType'].includes(key)) {
        // TODO: what if a security rule has both rootType and rootTag?
        allowedContentTypes = [...allowedContentTypes, ...value.split(',')];
      }
    });

    const rootTypeOrTag = rawUrl.searchParams.get('rootType') || rawUrl.searchParams.get('rootTag');
    const rootPublished = rawUrl.searchParams.get('rootPublished');

    if (rootTypeOrTag && rootPublished !== 'false') {
      allowedPublishedEdits.push(...rootTypeOrTag.split(','));
    }
  });
  return {
    allowedContentTags: [...new Set(allowedContentTags)],
    allowedContentTypes: [...new Set(allowedContentTypes)],
    allowedPublishedEdits: [...new Set(allowedPublishedEdits)],
    hasFullAccess: rRawContent.some((rawUrl) =>
      (rawUrl.pathname + rawUrl.search).match(/^\/content(?:\?\$\$meta.deleted=any)?$/g),
    ),
  };
};

export const getAllowedTypes = (
  securityData: Record<string, string[]> | null,
): Array<{
  type: NodeType;
  isSearchable: boolean;
  isCreatable: boolean;
  isCloneable: boolean;
  isPublishedEditable: boolean;
  category: RootNodeCategory | undefined;
}> => {
  const resourcesRawForUpdate = securityData ? securityData.UPDATE_CONTENT : [];
  const { hasFullAccess, allowedContentTags, allowedContentTypes, allowedPublishedEdits } =
    getAllowedContentTypesAndTags(resourcesRawForUpdate);

  return Object.entries(nodeTypeDefinitions)
    .filter(
      ([_type, typeDef]: [string, any]) =>
        hasFullAccess ||
        (typeDef.node?.type && allowedContentTypes.includes(typeDef.node.type)) ||
        (typeDef.node?.tags && allowedContentTags.includes(typeDef.node.tags[0])),
    )
    .map(([type, typeDef]: [string, any]) => {
      const typeConfig = nodeTypeConfigurations[type as NodeType];
      return {
        type: type as NodeType,
        isSearchable: 'isSearchable' in typeConfig && typeConfig.isSearchable,
        isCreatable: 'isCreatable' in typeConfig && typeConfig.isCreatable,
        isCloneable: 'isCloneable' in typeConfig && typeConfig.isCloneable,
        isPublishedEditable: Boolean(
          hasFullAccess ||
            (typeDef.node?.type && allowedPublishedEdits.includes(typeDef.node.type)) ||
            (typeDef.node?.tags && allowedPublishedEdits.includes(typeDef.node.tags[0])),
        ),
        category:
          ('category' in typeConfig.information && typeConfig.information.category) || undefined,
      };
    });
};

const wwwTypes: NodeType[] = [
  NodeType.WWW,
  NodeType.PRESS,
  NodeType.JOB_OFFER,
  NodeType.WWWNEWSITEM,
];

export type NodeTypeSecurity = {
  // isSearchable: boolean;
  // isCreatable: boolean;
  // isCloneable: boolean;
  isEditable: boolean;
  isPublishedEditable: boolean;
  isSuggestionsAllowed: boolean;
  isReviewAllowed: boolean;
};

export type NodeTypeSecurityMap = Partial<Record<NodeType, NodeTypeSecurity>>;

export const getNodeTypeSecurityMap = (
  securityData: Record<string, string[]> | null,
  canSuggest: boolean,
  canReview: boolean,
  proUserRoles: UserRole[],
): NodeTypeSecurityMap => {
  const resourcesRawForUpdate = securityData ? securityData.UPDATE_CONTENT : [];
  const { hasFullAccess, allowedContentTags, allowedContentTypes, allowedPublishedEdits } =
    getAllowedContentTypesAndTags(resourcesRawForUpdate);

  const nodeTypeSecurityMap: NodeTypeSecurityMap = {};
  Object.entries(nodeTypeDefinitions).forEach(([type, typeDef]: [string, any]) => {
    const typeConfig = nodeTypeConfigurations[type as NodeType];

    if (!isRootNodeConfig(typeConfig)) {
      return;
    }

    const baseConfig = isExtendedRootNodeConfig(typeConfig)
      ? nodeTypeConfigurations[typeConfig.extends]
      : typeConfig;

    const hasAccess =
      hasFullAccess ||
      (typeDef.node?.type && allowedContentTypes.includes(typeDef.node.type)) ||
      (typeDef.node?.tags && allowedContentTags.includes(typeDef.node.tags[0]));

    if (!hasAccess) {
      nodeTypeSecurityMap[type as NodeType] = {
        // isSearchable: false,
        // isCreatable: false,
        // isCloneable: false,
        isEditable: false,
        isPublishedEditable: false,
        isSuggestionsAllowed: false,
        isReviewAllowed: false,
      };
      return;
    }

    // * TODO define WWW user roles in security because you can be pro reviewer but not a www reviewer
    // and then you will still get into Review mode
    // * for security there is only the whole pro which is everything about pro including news and there is www
    // if you can allowSuggestions and it is not WWW then we assume it is Pro
    const isReviewer =
      (wwwTypes.includes(type as NodeType) && canReview) ||
      proUserRoles.includes('pro_webmaster') ||
      proUserRoles.includes('pro_editor') ||
      hasFullAccess;

    const typeSecurity = {
      // the first 3 are for the list screen
      // isSearchable: 'isSearchable' in typeConfig && typeConfig.isSearchable,
      // isCreatable: 'isCreatable' in typeConfig && typeConfig.isCreatable,
      // isCloneable: 'isCloneable' in typeConfig && typeConfig.isCloneable,

      isEditable: true, // if we have access to it, we can edit it
      isPublishedEditable: Boolean(
        hasFullAccess ||
          (typeDef.node?.type && allowedPublishedEdits.includes(typeDef.node.type)) ||
          (typeDef.node?.tags && allowedPublishedEdits.includes(typeDef.node.tags[0])),
      ),
      // suggestions and reviewers are a mess. a pro_webmaster can review everything that allows suggestions now. More roles are needed.
      isSuggestionsAllowed:
        'allowSuggestions' in baseConfig && baseConfig.allowSuggestions && canSuggest,
      isReviewAllowed:
        'allowSuggestions' in baseConfig && baseConfig.allowSuggestions && isReviewer,
    };
    nodeTypeSecurityMap[type as NodeType] = typeSecurity;
  });

  return nodeTypeSecurityMap;
};
